import { faClock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import * as tzmoment from 'moment-timezone';
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import companyTimeslots from './utils/companyOpeningTimeslots';
import companyClosingTime from './utils/companyClosingTime';

const TIMESLOTS = [
  'Off',
  '00:00AM',
  '00:30AM',
  '01:00AM',
  '01:30AM',
  '02:00AM',
  '02:30AM',
  '03:00AM',
  '03:30AM',
  '04:00AM',
  '04:30AM',
  '05:00AM',
  '05:30AM',
  '06:00AM',
  '06:30AM',
  '07:00AM',
  '07:30AM',
  '08:00AM',
  '08:30AM',
  '09:00AM',
  '09:30AM',
  '10:00AM',
  '10:30AM',
  '11:00AM',
  '11:30AM',
  '12:00PM',
  '12:30PM',
  '13:00PM',
  '13:30PM',
  '14:00PM',
  '14:30PM',
  '15:00PM',
  '15:30PM',
  '16:00PM',
  '16:30PM',
  '17:00PM',
  '17:30PM',
  '18:00PM',
  '18:30PM',
  '19:00PM',
  '19:30PM',
  '20:00PM',
  '20:30PM',
  '21:00PM',
  '21:30PM',
  '22:00PM',
  '22:30PM',
  '23:00PM',
  '23:30PM',
];

/**
 *
 * @param {string} timeSlot - The current timeslot
 * @param {function} setTimeSlot - The function to set the timeslot
 * @param {string} selectedDay - The selected day
 * @param {boolean} isAdhoc - Whether the booking is adhoc or not
 * @returns {JSX.Element} - The timeslot picker component
 */
const TimeslotPicker = ({
  timeSlot,
  setTimeSlot,
  selectedDay,
  isAdhoc,
  selectedEmployees,
  bookingTimeRange,
  openingTime,
  closingTime,
  timezone,
  selectedCompany,
  timeslots,
  tripType
}) => {
  const [viewOptions, setViewOptions] = useState(false);
  const [timeslotSettings, setTimeslotSettings] = useState([]);

  const dropdownRef = useRef(null);
  const boxRef = useRef(null);

  const selectedDate = tzmoment.tz(selectedDay, 'DD-MM-YYYY', timezone);
  const today = timezone ? tzmoment.tz(timezone).startOf("day") : moment().startOf("day");
  const tomorrow = timezone ? tzmoment.tz(timezone).add(1, "days").startOf("day") : moment().add(1, "days").startOf("day");

  const isToday = selectedDate.isSame(today, 'day');
  const isTomorrow = selectedDate.isSame(tomorrow, 'day');

  // Calculate if selected day is in the current week (for opening time filtering)
  const currentWeekStart = timezone ? tzmoment.tz(timezone).startOf("week") : moment().startOf("week");
  const currentWeekEnd = timezone ? tzmoment.tz(timezone).endOf("week") : moment().endOf("week");
  const isSelectedWeekCurrentWeek = selectedDate.isSameOrAfter(currentWeekStart) && selectedDate.isSameOrBefore(currentWeekEnd);

  // Only check if company has closed if the selected date is tomorrow and we're in the current week
  const hasCompanyClosed = isTomorrow && isSelectedWeekCurrentWeek ? companyClosingTime(moment(), timezone, closingTime) : false;

  const currentHour = timezone ? tzmoment.tz(timezone).hour() : moment().hour();
  const currentMinute = timezone ? tzmoment.tz(timezone).minute() : moment().minute();

  // Calculate 22 hours ago (2 hours into next day) - don't mutate the original moment
  const twentyTwoHoursAgo = timezone ? tzmoment.tz(timezone).subtract(22, "hours") : moment().subtract(22, "hours");
  
  const filterTimeslots = (timeslots) => {
    return timeslots.filter((slotObj) => {
      const slot = slotObj.timeslot || slotObj;

      if (isAdhoc) {
        return true; // Return all timeslots for adhoc bookings
      } else if (slot == '' || slot == 'Off' || slot == 'Clear') {
        // If the slot is empty or Off, return it since it works as the clear/cancel option
        return true;
      } else if (isTomorrow) {
        // Check if it's tomorrow - show timeslots 2 hours ahead (22 hours ago calculation)
        // The 22 hours ago trick: if it's 2:00 PM now, 22 hours ago was 4:00 PM yesterday
        // This means we show timeslots from 4:00 PM onwards for tomorrow
        const optionTime = moment(slot, 'hh:mmA');
        return optionTime >= twentyTwoHoursAgo;
      } else if (isToday) {
        // Check if it's today - show timeslots from bookingTimeRange hours ahead
        const optionTime = moment(slot, 'hh:mmA');
        const optionHour = optionTime.hour();
        const optionMinute = optionTime.minute();

        // Convert current time to minutes for easier comparison
        const currentTimeInMinutes = currentHour * 60 + currentMinute;
        const optionTimeInMinutes = optionHour * 60 + optionMinute;
        const bookingRangeInMinutes = bookingTimeRange * 60;

        return optionTimeInMinutes >= (currentTimeInMinutes + bookingRangeInMinutes);
      } else {
        // For all other days (including future dates), show all timeslots
        return true;
      }
    });
  };

  // Get allowed timeslots based on selected employee roles and trip_type
  const getAllowedTimeslotsForEmployees = () => {
    if (!timeslots || !selectedEmployees || selectedEmployees.length === 0) {
      return timeslots || [];
    }

    // Filter timeslots by trip_type if provided
    let filteredByTripType = timeslots;
    if (tripType && selectedCompany?.timeslot_limits) {
      filteredByTripType = timeslots.filter(slot => {
        // Include slots that match the trip_type or are generic (trip_type is null)
        return slot.trip_type === tripType || slot.trip_type === null;
      });
    }

    // Get all roles from selected employees
    const employeeRoles = [...new Set(selectedEmployees.map(emp => emp.role))];
    
    // Get allowed timeslots for each role
    const managerAllowed = filteredByTripType.filter(slot => slot.manager_permission).map(slot => slot.timeslot);
    const employeeAllowed = filteredByTripType.filter(slot => slot.employee_permission).map(slot => slot.timeslot);
    const companyAdminAllowed = filteredByTripType.filter(slot => slot.company_admin_permission).map(slot => slot.timeslot);

    // Find the intersection of allowed timeslots for all selected employee roles
    let allowedTimeslots = [];
    
    employeeRoles.forEach(role => {
      let roleAllowedTimeslots = [];
      
      switch (role) {
        case 'manager':
          roleAllowedTimeslots = managerAllowed;
          break;
        case 'employee':
          roleAllowedTimeslots = employeeAllowed;
          break;
        case 'company_admin':
          roleAllowedTimeslots = companyAdminAllowed;
          break;
        default:
          // Return all timeslots for other roles
          roleAllowedTimeslots = filteredByTripType.map(slot => slot.timeslot);
      }
      
      if (allowedTimeslots.length === 0) {
        allowedTimeslots = roleAllowedTimeslots;
      } else {
        // Find intersection with existing allowed timeslots
        allowedTimeslots = allowedTimeslots.filter(timeslot => 
          roleAllowedTimeslots.includes(timeslot)
        );
      }
    });

    // Filter the original timeslots to only include the allowed ones
    const filteredTimeslots = filteredByTripType.filter(slot => allowedTimeslots.includes(slot.timeslot));
    
    // Always include the "Off" option for bulk cancellations
    const offOption = { timeslot: 'Off', manager_permission: true, employee_permission: true, company_admin_permission: true };
    
    return [offOption, ...filteredTimeslots];
  };

  // Get timeslots filtered by employee roles only
  const roleFilteredTimeslots = getAllowedTimeslotsForEmployees();
  
  // Determine base timeslots to use
  let baseTimeslots = [];
  if (isAdhoc || !selectedCompany?.timeslot_limits) {
    // Use TIMESLOTS constant when timeslot_limits is off or for adhoc bookings
    baseTimeslots = TIMESLOTS;
  } else if (timeslotSettings.length > 0) {
    // Use timeslotSettings when available and timeslot_limits is on
    // Filter by trip_type if provided, then deduplicate by timeslot
    let filteredSettings = timeslotSettings;
    if (tripType && (tripType === 'inbound' || tripType === 'outbound')) {
      // Include slots that match the trip_type or are generic (trip_type is null)
      filteredSettings = timeslotSettings.filter(slot => {
        return slot.trip_type === tripType || slot.trip_type === null;
      });
    }
    // Extract timeslots and deduplicate
    const timeslotSet = new Set();
    baseTimeslots = filteredSettings
      .map(item => item.timeslot || item)
      .filter(timeslot => {
        if (timeslotSet.has(timeslot)) {
          return false; // Skip duplicates
        }
        timeslotSet.add(timeslot);
        return true;
      });
  } else if (timeslots && timeslots.length > 0) {
    // Fall back to timeslots prop if settings not available
    // Filter by trip_type if provided, then deduplicate
    let filteredTimeslots = timeslots;
    if (tripType && selectedCompany?.timeslot_limits && (tripType === 'inbound' || tripType === 'outbound')) {
      filteredTimeslots = timeslots.filter(slot => {
        return slot.trip_type === tripType || slot.trip_type === null;
      });
    }
    // Extract timeslots and deduplicate
    const timeslotSet = new Set();
    baseTimeslots = filteredTimeslots
      .map(item => item.timeslot || item)
      .filter(timeslot => {
        if (timeslotSet.has(timeslot)) {
          return false; // Skip duplicates
        }
        timeslotSet.add(timeslot);
        return true;
      });
  } else {
    // Final fallback to TIMESLOTS
    baseTimeslots = TIMESLOTS;
  }

  // Apply opening time filtering if needed (only when cutoff has passed and it's tomorrow in current week)
  // Use the imported companyTimeslots function for consistency with WeeklyTable
  const dayName = selectedDate.format('dddd').toLowerCase();
  const shouldFilterForOpeningTime = isTomorrow && hasCompanyClosed && isSelectedWeekCurrentWeek;
  let companyAvailableTimeslots = shouldFilterForOpeningTime && openingTime
    ? companyTimeslots(baseTimeslots, openingTime.replace(/\s/g,''))
    : baseTimeslots;

  // Apply time-based filtering (2 hours ahead, etc.)
  const timeFilteredTimeslots = filterTimeslots(companyAvailableTimeslots);

  // Apply role filtering if timeslot_limits is enabled and not adhoc
  let finalTimeslots;
  if (isAdhoc || !selectedCompany?.timeslot_limits) {
    finalTimeslots = timeFilteredTimeslots;
  } else {
    // Apply role filtering to time-filtered timeslots for non-adhoc bookings
    finalTimeslots = timeFilteredTimeslots.filter(slot => {
      const slotValue = slot.timeslot || slot;
      return roleFilteredTimeslots.some(roleSlot => roleSlot.timeslot === slotValue);
    });
    
    // Always ensure "Off" option is available for bulk cancellations
    if (!finalTimeslots.some(slot => (slot.timeslot || slot) === 'Off')) {
      finalTimeslots = ['Off', ...finalTimeslots];
    }
  }

  // Convert to consistent format for rendering
  const finalRegularTimeslots = finalTimeslots;
  const finalSettingsTimeslots = [];

  const toggleView = () => {
    setViewOptions(!viewOptions);
  };

  const handleSelect = (slot) => {
    setViewOptions(false);
    setTimeSlot(slot);
  };

  // Fetch the saved timeslot permissions from timeslot_settings table
  useEffect(() => {
    // Do not fetch timeslot settings until the selectedCompany is set
    if (!selectedCompany || !selectedCompany.id) return;

    axiosInstance.get(`/companies/${selectedCompany.id}/timeslot_settings.json`)
      .then(response => {
        setTimeslotSettings(response.data);
      })
      .catch(error => {
        console.error('Error fetching timeslot settings:', error);
      });
  }, [selectedCompany?.id]);

  useEffect(() => {
    // Function to handle the click outside the component
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        boxRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !boxRef.current.contains(event.target)
      ) {
        setViewOptions(false);
      }
    };

    // Add the event listener when the component mounts
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div
        className="timeslot flex justify-between items-center bg-white border border-gray-200 text-gray-500 rounded-lg px-2 py-1 relative"
        onClick={selectedEmployees?.length > 0 ? toggleView : null}
        style={{ userSelect: 'none' }}
        ref={boxRef}>
        <div className="text-gray-600" style={{ fontSize: '14px' }}>
          {timeSlot ? timeSlot : 'Select a Timeslot'}
        </div>
        <FontAwesomeIcon icon={faClock} className="text-gray-600" />
        {viewOptions && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 bg-white rounded-lg shadow-md border border-gray-300 w-full overflow-y-auto justify-center items-center z-40"
            style={{ top: 'calc(100%)', maxHeight: '250px' }}>
            {selectedEmployees && selectedEmployees.length > 0 && (
              <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                Showing timeslots available for {selectedEmployees.length} selected employee{selectedEmployees.length > 1 ? 's' : ''}
              </div>
            )}
              <div
                className="px-4 py-1 text-gray-600 font-normal cursor-pointer hover:bg-gray-100"
                style={{ fontSize: '14px', minHeight: '28px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect('Off');
                }}>Off
              </div>
            {finalRegularTimeslots.map((item, index) => {
              const value = item.time || item.timeslot || item.name || item;
              if (value === 'Off') return null;
              return (
                <div
                  key={index}
                  className="px-4 py-1 text-gray-600 font-normal cursor-pointer hover:bg-gray-100"
                  style={{ fontSize: '14px', minHeight: '28px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(value);
                  }}>
                  {value != '' && value}
                </div>
              );
            })}
            {finalRegularTimeslots.length === 0 && (
              <div className="px-4 py-2 text-xs text-gray-400 text-center">
                No timeslots available for selected employees
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TimeslotPicker;
